import { call, put, takeLatest } from "redux-saga/effects";
import { axiosRestInstanceForAI } from "../../../common/axios";
import { AxiosResponse } from "axios";
import { sendMessageSagaAction, chatbotSagaActionTypes } from "./saga-actions";
import { chatbotSliceAction } from "../../slices/chatbot";
import { SendMessageRequest, SendMessageResponse, ChatHistoryResponse, IndividualChatHistoryResponse } from "../../slices/chatbot/Types";

// ----------------------------------------
// API
const sendMessageAPI = async (payload: SendMessageRequest) => {
  const response = await axiosRestInstanceForAI.post<
    AxiosResponse<SendMessageResponse>
  >(`/ai/message`, payload);
  return response.data;
};

const fetchChatHistoryAPI = async () => {
  const response = await axiosRestInstanceForAI.get<
    AxiosResponse<ChatHistoryResponse>
  >(`/ai/sessions`);
  return response.data;
};

const fetchIndividualChatHistoryAPI = async (conversationSessionId: string) => {
  const response = await axiosRestInstanceForAI.get<
    AxiosResponse<IndividualChatHistoryResponse>
  >(`/ai/chatHistory/${conversationSessionId}`);
  return response.data;
};

// ----------------------------------------
// Sagas

function* sendMessageSaga(action: {
  type: string;
  message: string;
}) {
  try {
    yield put(chatbotSliceAction.setLoading(true));
    yield put(chatbotSliceAction.setError({ error: false, errorMessage: null }));
    
    const payload: SendMessageRequest = {
      message: action.message,
    };
    
    const response: SendMessageResponse = yield call(
      sendMessageAPI,
      payload
    );
    
    if (response.success && response.data) {
      yield put(chatbotSliceAction.setMessageResponse(response.data));
    } else {
      yield put(
        chatbotSliceAction.setError({
          error: true,
          errorMessage: "Failed to get response from chatbot",
        })
      );
    }
  } catch (e: any) {
    console.error("Error sending message to chatbot:", e);
    yield put(
      chatbotSliceAction.setError({
        error: true,
        errorMessage: e.response?.data?.message || e.message || "An error occurred while sending message",
      })
    );
  } finally {
    yield put(chatbotSliceAction.setLoading(false));
  }
}

function* fetchChatHistorySaga() {
  try {
    yield put(chatbotSliceAction.setChatHistoryLoading(true));
    yield put(chatbotSliceAction.setChatHistoryError({ error: false, errorMessage: null }));
    
    const response: ChatHistoryResponse = yield call(fetchChatHistoryAPI);
    
    if (response.success && response.data) {
      yield put(chatbotSliceAction.setChatHistory(response.data));
    } else {
      yield put(
        chatbotSliceAction.setChatHistoryError({
          error: true,
          errorMessage: "Failed to fetch chat history",
        })
      );
    }
  } catch (e: any) {
    console.error("Error fetching chat history:", e);
    yield put(
      chatbotSliceAction.setChatHistoryError({
        error: true,
        errorMessage: e.response?.data?.message || e.message || "An error occurred while fetching chat history",
      })
    );
  } finally {
    yield put(chatbotSliceAction.setChatHistoryLoading(false));
  }
}

function* fetchIndividualChatHistorySaga(action: {
  type: string;
  conversationSessionId: string;
}) {
  try {
    yield put(chatbotSliceAction.setIndividualChatHistoryLoading(true));
    yield put(chatbotSliceAction.setIndividualChatHistoryError({ error: false, errorMessage: null }));
    
    const response: IndividualChatHistoryResponse = yield call(
      fetchIndividualChatHistoryAPI,
      action.conversationSessionId
    );
    
    if (response.success && response.data) {
      yield put(chatbotSliceAction.setIndividualChatHistory(response.data));
    } else {
      yield put(
        chatbotSliceAction.setIndividualChatHistoryError({
          error: true,
          errorMessage: "Failed to fetch individual chat history",
        })
      );
    }
  } catch (e: any) {
    console.error("Error fetching individual chat history:", e);
    yield put(
      chatbotSliceAction.setIndividualChatHistoryError({
        error: true,
        errorMessage: e.response?.data?.message || e.message || "An error occurred while fetching individual chat history",
      })
    );
  } finally {
    yield put(chatbotSliceAction.setIndividualChatHistoryLoading(false));
  }
}

// ----------------------------------------
// Root Saga

function* chatbotSaga() {
  yield takeLatest(
    chatbotSagaActionTypes.callSendMessageSaga,
    sendMessageSaga
  );
  yield takeLatest(
    chatbotSagaActionTypes.callFetchChatHistorySaga,
    fetchChatHistorySaga
  );
  yield takeLatest(
    chatbotSagaActionTypes.callFetchIndividualChatHistorySaga,
    fetchIndividualChatHistorySaga
  );
}

export default chatbotSaga;
